import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ArrowSquareOut, Robot } from 'phosphor-react';
import { IStatusGeracaoV2, useSocietario } from '../../../../../hooks/useSocietario';

export type GerarContratoLoadingModalTerminal = {
  // Pos-Fase 6 (gap dos validators): `em_revisao` = DOCX gerado mas com
  // criticos NAO-overridable bloqueando aceitacao.
  status: 'gerado' | 'em_revisao' | 'campos_pendentes' | 'falha_geracao' | 'no_task';
  source: 'websocket' | 'polling';
  data?: any;
};

interface GerarContratoLoadingModalProps {
  isOpen: boolean;
  contratoId: number | null;
  /**
   * Disparado quando a geracao chega em estado terminal. O parent decide
   * se fecha o modal, navega, mostra toast etc. O modal so chama uma vez.
   */
  // eslint-disable-next-line no-unused-vars
  onTerminal: (final: GerarContratoLoadingModalTerminal) => void;
  /** Erros transitorios de polling. Modal segue tentando — apenas log/toast. */
  // eslint-disable-next-line no-unused-vars
  onError?: (err: Error) => void;
  /**
   * Sessao 2026-05-08 (Fase C): permite o operador fechar o modal e continuar
   * navegando enquanto a IA gera em background. Quando o backend conclui,
   * `NotificationProvider` recebe via WS e mostra toast + persiste em
   * `tb_notificacoes`. Se nao informado, modal segue obrigatoriamente aberto
   * ate evento terminal (comportamento antigo).
   */
  onDismiss?: () => void;
}

// Mensagens contextuais rotativas a cada ~6s. So texto — back end nao expoe
// fase do pipeline em tempo real, entao usamos heuristica baseada em tempo.
const FASES = [
  'Carregando modelo de referência e exemplos…',
  'A IA está estruturando as cláusulas (pode levar até 60s)…',
  'Validando estrutura e regras DREI…',
  'Renderizando o DOCX final…',
  'Quase lá — finalizando persistência…',
];

// Sessao 2026-05-14 (review Matias PR #345 #3): teto absoluto da geracao.
// Sem deadline o modal ficava aberto pra sempre se a task travasse no backend
// com `processing` (WS caido + polling sempre retornando in-progress) — operador
// nao tinha como escapar sem reload do navegador. 5 min cobre folgadamente o
// p95 observado (~90s) e ainda da headroom pra picos.
const MAX_GENERATION_MS = 5 * 60 * 1000;

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const mm = Math.floor(totalSec / 60)
    .toString()
    .padStart(2, '0');
  const ss = (totalSec % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

export const GerarContratoLoadingModal = ({
  isOpen,
  contratoId,
  onTerminal,
  onError,
  onDismiss,
}: GerarContratoLoadingModalProps) => {
  const { subscribeGeracaoStatus } = useSocietario();
  const [elapsedMs, setElapsedMs] = useState(0);
  const [faseIdx, setFaseIdx] = useState(0);
  const startedAtRef = useRef<number | null>(null);
  // Refs estaveis para callbacks — evita re-subscribe a cada render do parent.
  const onTerminalRef = useRef(onTerminal);
  const onErrorRef = useRef(onError);
  // Sessao 2026-05-14 (review Matias PR #345 #3): flag pra impedir que o
  // terminal-do-hook dispare DEPOIS que o deadline ja forcou um terminal
  // (parent so deve receber 1 chamada). O dedup interno do hook so cobre
  // origens WS/polling, nao o deadline imposto aqui.
  const deadlineFiredRef = useRef(false);
  useEffect(() => {
    onTerminalRef.current = onTerminal;
  }, [onTerminal]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // ⚠️ O relogio comeca quando o MODAL ABRE, nao quando a geracao comeca — ver
  // o efeito logo abaixo. Antes ele era zerado aqui, e como esta etapa so roda
  // com `contratoId` (ou seja, ja na geracao), o operador via 00:00 parado
  // durante toda a validacao e o parse, que sao justamente a parte demorada.
  // Zerar aqui tambem descartaria o tempo ja decorrido no preparo.
  useEffect(() => {
    if (!isOpen || contratoId == null) return undefined;

    const cleanup = subscribeGeracaoStatus({
      contratoId,
      onUpdate: (_status: IStatusGeracaoV2) => {
        // Tick a cada poll para tirar o estado de "stuck" se o setInterval
        // do timer falhar. Tambem sinaliza vida da conexao.
      },
      onTerminal: final => {
        if (deadlineFiredRef.current) return;
        onTerminalRef.current(final);
      },
      onError: err => {
        onErrorRef.current?.(err);
      },
    });
    return cleanup;
    // subscribeGeracaoStatus e estavel (referencia do hook); contratoId e isOpen
    // sao os unicos triggers reais.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, contratoId]);

  // Relogio e fases arrancam na ABERTURA do modal — que hoje acontece ja no
  // clique em Gerar, antes da validacao e do parse.
  useEffect(() => {
    if (!isOpen) {
      startedAtRef.current = null;
      return;
    }
    startedAtRef.current = Date.now();
    setElapsedMs(0);
    setFaseIdx(0);
    deadlineFiredRef.current = false;
  }, [isOpen]);

  // Timer de tela — independente do polling pra UI nao parecer travada.
  // Tambem aplica o teto absoluto: se a geracao passar de MAX_GENERATION_MS
  // sem terminar, forca um terminal `falha_geracao` pra desbloquear o operador.
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = setInterval(() => {
      if (startedAtRef.current == null) return;
      const elapsed = Date.now() - startedAtRef.current;
      setElapsedMs(elapsed);
      if (elapsed >= MAX_GENERATION_MS && !deadlineFiredRef.current) {
        deadlineFiredRef.current = true;
        onTerminalRef.current({
          status: 'falha_geracao',
          source: 'polling',
          data: {
            error:
              'A geração excedeu o tempo máximo de 5 minutos sem confirmação do backend. ' +
              'Confira a listagem de contratos — pode ter sido concluída em background — ou tente regerar.',
          },
        });
      }
    }, 500);
    return () => clearInterval(id);
  }, [isOpen]);

  // Rotacao das mensagens contextuais.
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = setInterval(() => {
      setFaseIdx(prev => Math.min(prev + 1, FASES.length - 1));
    }, 6000);
    return () => clearInterval(id);
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => undefined}
      size="md"
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Robot size={22} color="#2563EB" weight="duotone" />
            <Text>Gerando contrato com IA</Text>
          </HStack>
        </ModalHeader>
        <ModalBody pb={6}>
          <VStack align="stretch" spacing={4}>
            <Flex align="center" gap={4}>
              <Spinner size="lg" color="blue.500" thickness="3px" />
              <Box>
                <Text fontSize="sm" color="gray.700" fontWeight="medium">
                  {FASES[faseIdx]}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1} fontFamily="mono">
                  Tempo decorrido: {formatElapsed(elapsedMs)}
                </Text>
              </Box>
            </Flex>

            <Progress size="xs" isIndeterminate colorScheme="blue" borderRadius="full" />

            <Box bg="blue.50" borderRadius="md" p={3}>
              <Text fontSize="xs" color="blue.800">
                {/* A estimativa era 30–90s, calibrada no v2. Medido no motor
                    novo em 2026-08-19: ~11s quando a leitura do vigente ja esta
                    em cache, e mais de 2min na primeira geracao do contrato,
                    que paga a leitura por IA. Prometer 90s e depois passar
                    disso faz o operador achar que travou. */}
                A primeira geração de um contrato leva alguns minutos; as seguintes são bem mais
                rápidas.{' '}
                {onDismiss
                  ? 'Você pode continuar usando o sistema — a notificação chega aqui e no painel quando concluir.'
                  : 'Não feche esta janela — você será notificado automaticamente quando o contrato estiver pronto.'}
              </Text>
            </Box>
          </VStack>
        </ModalBody>
        {onDismiss && (
          <ModalFooter pt={2}>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<ArrowSquareOut size={14} weight="bold" />}
              onClick={onDismiss}
            >
              Fechar e continuar em background
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default GerarContratoLoadingModal;
